/**
 * What an import file is allowed to contain, and how its columns reach the API.
 *
 * The spreadsheet has its own column names and they are not the API's. They
 * were the same once, which is why renaming a field broke every file people
 * had: the headers travel into the request as keys, so `mp_nome` in a sheet
 * became `mp_nome` in the payload. Keeping the two apart means the API can be
 * renamed - and it is about to be, wholesale, into English - without anybody
 * having to touch a file they filled in last year.
 *
 * `file` is the contract with whoever fills in the spreadsheet. `translateFileHeadersToApiFields` is the
 * translation, applied on the way out and nowhere else.
 *
 * `translateFileHeadersToApiFields` names every column, including the ones spelled the same on both
 * sides. The mapping used to hold only the columns that differed, and the
 * three that did not - `cas`, `ec`, `cod_mp` - travelled by falling through it,
 * which reads as agreement and is really an absence. Renaming `cod_mp` to
 * `code` in the database was then invisible here: the header still matched, no
 * stale entry pointed anywhere, and the column simply arrived at the API under
 * a name it no longer knew. Written out in full there is one line per column
 * and one place to edit, and identity is stated rather than implied.
 *
 * So there is no falling through any more, here or in `toApiPayload`: a column
 * the map does not name stops the import instead of travelling under a name
 * nobody chose.
 */

export const RAW_MATERIALS = {
  // Verified against app/src/templates/materias-primas.xlsx, which is the file
  // the customer downloads: these seven strings are its header row, in
  // Portuguese, and renaming a column in the database never renames them.
  file: ['cod_mp', 'mp_nome', 'tipo', 'cas', 'ec', 'observacoes', 'perigosa'],
  translateFileHeadersToApiFields: {
    cod_mp: 'code',
    mp_nome: 'name',
    tipo: 'type',
    cas: 'cas',
    ec: 'ec',
    observacoes: 'observations',
    perigosa: 'hazardous'
  }
};

export const SUPPLIERS = {
  // `file` is what the customer's spreadsheet says, and the template they
  // downloaded years ago still says it. It is not ours to rename: the header
  // stays Portuguese and `translateFileHeadersToApiFields` carries it to whatever the API calls it now.
  file: ['cod_fornecedor', 'fornecedor', 'ativo'],
  translateFileHeadersToApiFields: {
    cod_fornecedor: 'code',
    fornecedor: 'name',
    ativo: 'active'
  }
};

/**
 * A header Excel left behind rather than one somebody typed.
 *
 * Dragging a selection or deleting a column's contents leaves the column in the
 * sheet, and it arrives as an empty or absent name. Those are ignored: the rule
 * is about the columns a person wrote, not about what the spreadsheet dragged
 * along.
 */
const isEmptyHeader = header =>
  header === undefined || header === null || String(header).trim() === '' ||
  String(header) === 'undefined';

/**
 * Phase one: the file has exactly the expected columns, no more and no fewer.
 *
 * Runs before any other check, because every other check reads a column by
 * name: with the wrong headers they all fail at once, and the reader is left
 * with a page of errors about values when the problem is the shape.
 *
 * Returns [] when the columns are right, so the caller can carry on.
 */
export const checkColumns = (rows, expected, fileType) => {
  const present = rows.length === 0 ? [] :
    Object.keys(rows[0]).filter(header => !isEmptyHeader(header));

  const missing = expected.filter(column => !present.includes(column));
  const unknown = present.filter(column => !expected.includes(column));

  const errors = [];

  if (rows.length === 0) {
    errors.push({
      fileType,
      column: '-',
      label: 'ficheiro',
      value: '-',
      message: 'O ficheiro não tem linhas.'
    });
    return errors;
  }

  missing.forEach(column => errors.push({
    fileType,
    column,
    label: 'coluna em falta',
    value: '-',
    message: `Falta a coluna "${column}". O ficheiro tem de ter exactamente: ${expected.join(', ')}.`
  }));

  unknown.forEach(column => errors.push({
    fileType,
    column,
    label: 'coluna desconhecida',
    value: '-',
    message: `A coluna "${column}" não é esperada. O ficheiro tem de ter exactamente: ${expected.join(', ')}.`
  }));

  return errors;
};

/**
 * Phase three: the file's names become the API's, on the way out.
 *
 * Every column is translated, and a column with no translation is a bug rather
 * than a column that keeps its name. It used to read `mapping[column] || column`,
 * which was unreachable - `checkColumns` has already refused anything the
 * contract does not list - but it read as though falling through were a normal
 * outcome, and that appearance is what made an unmapped column look fine on
 * 5 Aug while the import quietly lost it.
 *
 * Throwing rather than returning: without the `||`, an unmapped column would
 * write to the key `undefined` and send it, which is the same silence wearing a
 * worse name.
 */
export const toApiPayload = (rows, mapping) =>
  rows.map(row => Object.keys(row).reduce((out, column) => {
    const field = mapping[column];
    if (!field) {
      throw new Error(`import: the column "${column}" has no field in the contract`);
    }
    out[field] = row[column];
    return out;
  }, {}));

/**
 * What counts as yes in a spreadsheet.
 *
 * The column holds a boolean, but a person fills it in, and a person writes
 * `Sim`, `sim`, `Não`, `TRUE` or whatever Excel turned their typing into. All of
 * them mean one of two things, so all of them are accepted and normalised here,
 * to the strings the schema and the API both already understand.
 *
 * Anything else is returned untouched, on purpose: the validator then refuses it
 * by name and the reader is told which column and which value, instead of the
 * API answering "Must be a valid boolean" about a line number.
 */
const TRUE_WORDS = ['true', 'sim'];
const FALSE_WORDS = ['false', 'nao', 'não'];

export const toBooleanString = value => {
  // String() first, so the real booleans the template contains come through the
  // same path as the words somebody typed.
  const word = String(value).trim().toLowerCase();
  if (TRUE_WORDS.includes(word)) {
    return 'true';
  }
  if (FALSE_WORDS.includes(word)) {
    return 'false';
  }
  return value;
};

/**
 * The other direction: is this cell a yes?
 *
 * The inventory screens read a real boolean from the API, and the import preview
 * reads the string `sanitize` produced from the file. A plain `value ? ...`
 * answers yes to both, because the string 'false' is not empty - which is how a
 * row that says Não came to be displayed as Sim.
 */
export const isYes = value => value === true || String(value).trim().toLowerCase() === 'true';
